import React, { useState, useEffect, useContext } from 'react';
import { Container, Badge, Button, Modal, Alert } from 'react-bootstrap';
import { AuthContext } from '../context/AuthContext';
import TasksService from '../services/TasksService';
import ProfileImage from './ProfileImage';
import userService from '../services/userService';
import Spinner from './Spinner';
import ResponsiveTable from './ResponsiveTable';

const MyTasks = () => {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedTask, setSelectedTask] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [comments, setComments] = useState('');
    const [error, setError] = useState(null);
    const [canEdit, setCanEdit] = useState(false);
    const { user } = useContext(AuthContext);
    const [userDetailsCache, setUserDetailsCache] = useState({});

    useEffect(() => {
        if (user?.userId) {
            loadTasks();
        }
    }, [user]);

    const loadTasks = async () => {
        try {
            setError(null);
            const data = await TasksService.getMyTasks(user.userId);
            setTasks(data);
        } catch (error) {
            console.error('Error loading tasks:', error);
            setError(error.message);
        } finally {
            setLoading(false);
        }
    };

    const handleViewDetails = async (taskId) => {
        try {
            setError(null);
            const taskDetails = await TasksService.getTaskDetails(taskId, user.userId);
            setSelectedTask(taskDetails);
            
            // Check if user can edit their last action
            const editPermission = await TasksService.canEditTaskAction(taskId, user.userId);
            setCanEdit(editPermission);
            
            // Debug logging to help understand edit conditions
            console.log('=== EDIT DEBUG INFO ===');
            console.log('Task ID:', taskId);
            console.log('User ID:', user.userId);
            console.log('Task Status:', taskDetails?.status);
            console.log('Can Edit:', editPermission);
            console.log('Task Activities:', taskDetails?.activities);
            
            // Find user's last activity
            const userActivities = taskDetails?.activities?.filter(a => a.performedByUserId === user.userId);
            const userLastActivity = userActivities?.sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt))[0];
            console.log('User Last Activity:', userLastActivity);
            
            // Check if there are activities after user's last activity
            if (userLastActivity) {
                const laterActivities = taskDetails?.activities?.filter(a => 
                    a.performedByUserId !== user.userId && 
                    new Date(a.performedAt) > new Date(userLastActivity.performedAt)
                );
                console.log('Activities after user last action:', laterActivities);
            }
            console.log('=====================');
            
            setShowModal(true);
        } catch (error) {
            console.error('Error loading task details:', error);
            setError(error.message);
        }
    };

    const handleAction = async (action) => {
        try {
            setError(null);
            await TasksService.performTaskAction(selectedTask.id, action, comments, user.userId);
            setShowModal(false);
            setComments('');
            loadTasks(); // Reload tasks after action
        } catch (error) {
            console.error('Error performing action:', error);
            setError(error.message);
        }
    };

    const handleEditAction = async () => {
        try {
            setError(null);
            await TasksService.editTaskAction(selectedTask.id, user.userId);
            setShowModal(false);
            setCanEdit(false);
            setComments('');
            loadTasks(); // Reload tasks after edit
        } catch (error) {
            console.error('Error editing action:', error);
            setError(error.message);
        }
    };

    const getStatusBadgeVariant = (status) => {
        switch (status) {
            case 'InProgress':
                return 'primary';
            case 'Completed':
                return 'success';
            case 'Rejected':
                return 'danger';
            case 'Cancelled':
                return 'secondary';
            default:
                return 'warning';
        }
    };

    const getComplianceStatusVariant = (status) => {
        switch (status) {
            case 'OnTrack':
                return 'success';
            case 'AtRisk':
                return 'warning';
            case 'Breached':
                return 'danger';
            case 'Completed':
                return 'info';
            default:
                return 'secondary';
        }
    };

    const getPriorityVariant = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'urgent':
                return 'danger';
            case 'high':
                return 'warning';
            case 'normal':
                return 'info';
            case 'low':
                return 'secondary';
            default:
                return 'light';
        }
    };

    const calculateTimeRemaining = (dueDate) => {
        if (!dueDate) return null;
        const now = new Date();
        const due = new Date(dueDate);
        const diff = due - now;
        
        if (diff < 0) return 'Overdue';
        
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        
        if (hours > 48) return `${Math.floor(hours / 24)}d ${hours % 24}h`;
        if (hours > 0) return `${hours}h ${minutes}m`;
        return `${minutes}m`;
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        try {
            const date = new Date(dateString);
            return date.toLocaleString('en-US', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            console.error('Error formatting date:', error);
            return dateString;
        }
    };

    const formatAction = (action) => {
        if (!action) return '';
        if (action.toLowerCase().endsWith('ed')) return action;
        return `${action}ed`;
    };

    const UserDetailsDisplay = ({ userId }) => {
        const [details, setDetails] = useState(null);

        useEffect(() => {
            const fetchDetails = async () => {
                try {
                    if (!userDetailsCache[userId]) {
                        const data = await userService.getUserDetails(userId);
                        setUserDetailsCache(prev => ({ ...prev, [userId]: data }));
                        setDetails(data);
                    } else {
                        setDetails(userDetailsCache[userId]);
                    }
                } catch (error) {
                    console.error('Error fetching user details:', error);
                }
            };
            fetchDetails();
        }, [userId]);

        if (!details) return <div>Loading...</div>;

        return (
            <div className="d-flex align-items-center">
                <ProfileImage
                    imageUrl={details.imageUrl}
                    width={32}
                    height={32}
                    className="rounded-circle me-2"
                />
                <div>
                    <div>{details.fullName}</div>
                    <small className="text-muted">{details.email}</small>
                </div>
            </div>
        );
    };

    const columns = [
        { key: 'workflowName', label: 'Task Type' },
        { 
            key: 'initiatedByUserId', 
            label: 'Initiator',
            render: (value) => <UserDetailsDisplay userId={value} />
        },
        { key: 'currentStepName', label: 'Action Needed' },
        {
            key: 'priority',
            label: 'Priority',
            render: (value) => (
                <Badge bg={getPriorityVariant(value)} className="text-dark">
                    {value || 'Normal'}
                </Badge>
            )
        },
        {
            key: 'complianceStatus',
            label: 'SLA Status',
            render: (value, task) => (
                <div>
                    <Badge bg={getComplianceStatusVariant(value)} className="mb-2">
                        {value || 'OnTrack'}
                    </Badge>
                    {task.oLADueDate && (
                        <div>
                            <small className="text-muted d-block">
                                OLA: {calculateTimeRemaining(task.oLADueDate)}
                            </small>
                        </div>
                    )}
                </div>
            )
        },
        {
            key: 'isEscalated',
            label: 'Escalation',
            render: (value, task) => (
                value ? (
                    <Badge bg="danger">⚠️ Escalated</Badge>
                ) : (
                    <span className="text-muted">—</span>
                )
            )
        },
        { 
            key: 'status', 
            label: 'Status',
            render: (value) => (
                <Badge bg={getStatusBadgeVariant(value)}>
                    {value}
                </Badge>
            )
        }
    ];

    const actions = [
        {
            label: 'View Details',
            variant: 'info',
            onClick: (task) => handleViewDetails(task.id)
        }
    ];

    if (loading) {
        return <Spinner />;
    }

    return (
        <Container className="mt-4">
            
            {error && (
                <Alert variant="danger" className="mt-3">
                    {error}
                </Alert>
            )}
            
            {/* Debug Section - Remove this after debugging */}
            <Alert variant="info" className="mt-3">
                <h6>Debug: Edit Conditions</h6>
                <small>
                    <strong>Can Edit:</strong> {canEdit ? 'Yes' : 'No'}<br/>
                    <strong>Task Status:</strong> {selectedTask?.status}<br/>
                    <strong>Your Last Action:</strong> {selectedTask?.activities?.filter(a => a.performedByUserId === user.userId).sort((a, b) => new Date(b.performedAt) - new Date(a.performedAt))[0]?.action || 'None'}<br/>
                    <strong>Total Activities:</strong> {selectedTask?.activities?.length || 0}<br/>
                    <strong>Your Activities:</strong> {selectedTask?.activities?.filter(a => a.performedByUserId === user.userId).length || 0}
                </small>
            </Alert>
            
            <ResponsiveTable
                data={tasks}
                columns={columns}
                actions={actions}
            />

            {/* Task Details Modal */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>Task Details</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedTask && (
                        <>
                            {/* Suspension Info */}
                            {selectedTask.isSuspended && (
                                <div className="mt-3 p-2 border rounded bg-warning bg-opacity-10">
                                    <strong className="text-warning">🔒 Suspended</strong>
                                    {selectedTask.suspensionReason && (
                                        <p className="mb-0 text-muted small mt-1">Reason: {selectedTask.suspensionReason}</p>
                                    )}
                                </div>
                            )}

                            {/* Escalation Chain */}
                            {selectedTask.escalationChain && (
                                <div className="mt-2 small">
                                    <strong>Escalation Chain:</strong> {selectedTask.escalationChain}
                                </div>
                            )}

                            {/* Recovery Status */}
                            {selectedTask.wasBreachedButRecovered && (
                                <div className="mt-2">
                                    <Badge bg="success">✓ Recovered from breach</Badge>
                                </div>
                            )}

                            <div className="mb-4">
                                <h6>Request Details</h6>
                                <div className="card">
                                    <div className="card-body">
                                        {selectedTask.entityDetails && Object.entries(selectedTask.entityDetails).map(([key, value]) => {
                                            // Skip internal fields
                                            if (key === 'id' || key === 'leaveTypeId') {
                                                return null;
                                            }

                                            // Handle userId specially
                                            if (key === 'userId') {
                                                return (
                                                    <div key={key} className="mb-2">
                                                        <strong>User:</strong>{' '}
                                                        <UserDetailsDisplay userId={value} />
                                                    </div>
                                                );
                                            }

                                            // Format the display name
                                            const displayName = key
                                                .replace(/([A-Z])/g, ' $1') // Add space before capital letters
                                                .replace(/^./, str => str.toUpperCase()); // Capitalize first letter

                                            // Format the value based on field type
                                            let displayValue;
                                            if (key.toLowerCase().includes('date') || key === 'createdAt' || key === 'updatedAt') {
                                                displayValue = value ? formatDate(value) : '-';
                                            } else if (typeof value === 'boolean') {
                                                displayValue = value ? 'Yes' : 'No';
                                            } else if (key === 'totalDays') {
                                                displayValue = `${value} day${Math.abs(value) !== 1 ? 's' : ''}`;
                                            } else {
                                                displayValue = value || '-';
                                            }

                                            return (
                                                <div key={key} className="mb-2">
                                                    <strong>{displayName}:</strong>{' '}
                                                    {displayValue}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                            
                            <h6 className="mt-4">Activity History</h6>
                            <ResponsiveTable
                                data={selectedTask.activities || []}
                                columns={[
                                    { key: 'action', label: 'Action' },
                                    { key: 'comments', label: 'Comments' },
                                    { 
                                        key: 'performedAt', 
                                        label: 'Performed At',
                                        render: (value) => formatDate(value)
                                    }
                                ]}
                            />

                            <div className="mt-4">
                                <h6>Comments</h6>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={comments}
                                    onChange={(e) => setComments(e.target.value)}
                                />
                            </div>
                        </>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => {
                        setShowModal(false);
                        setCanEdit(false);
                        setComments('');
                    }}>
                        Close
                    </Button>
                    {canEdit && (
                        <Button variant="outline-primary" onClick={handleEditAction}>
                            Edit Last Action
                        </Button>
                    )}
                    <Button variant="success" onClick={() => handleAction('Approve')}>
                        Approve
                    </Button>
                    <Button variant="danger" onClick={() => handleAction('Reject')}>
                        Reject
                    </Button>
                    <Button variant="warning" onClick={() => handleAction('Cancel')}>
                        Cancel
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default MyTasks; 